import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import type { MailClient } from '../api/client';
import type { EmailBodyPart } from '../api/types';
import { decodeText, previewKind } from '../mail/preview';
import { fileSize } from '../mail/format';
import { useOverlay } from '../ui/overlays';
import { useAccounts } from '../auth/store';

/*
    Looking at an attachment without saving it first.

    Download-only is fine until someone is working through a day of invoices, at
    which point every glance costs a file on the desktop that has to be cleaned
    up later. Images and PDFs open here instead.

    Only those two types, and both are rendered from a blob: URL of bytes already fetched — a PDF
    goes into a sandboxed iframe (the browser's own viewer, no script from the document), an image
    into a plain <img>. Anything else still downloads, because "preview" for arbitrary types means
    running someone else's file, and mail attachments are exactly where you don't.

    The server agrees separately and independently: it will only serve a part for display if it is
    on its own short list, and everything else comes back as a download whatever this asks for. The
    two checks are deliberately not shared — this one decides what to show, that one decides what
    the browser is allowed to be handed.
*/

/** The server's own sentence about why, which is written to be read by a person. */
async function messageFrom(res: Response): Promise<string> {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    return body?.error ?? `${res.status}`;
}

export default function AttachmentPreview({
    client,
    emailId,
    attachments,
    index,
    onIndex,
    onClose,
}: {
    client: MailClient | undefined;
    /** The message these parts belong to. A part number means nothing without it. */
    emailId: string | undefined;
    /** The previewable ones only, in the order they appear on the message. */
    attachments: EmailBodyPart[];
    index: number;
    onIndex: (index: number) => void;
    onClose: () => void;
}) {
    const { t, lang } = useT();
    // While this is up, escape closes it and nothing behind it.
    useOverlay();
    const documents = useAccounts((s) => s.canPreviewDocuments);
    const [url, setUrl] = useState<string | null>(null);
    const [text, setText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const current = attachments[index];
    const kind = current ? previewKind(current, documents) : null;

    /*
        Three ways in, and the difference is deliberate.

        What the sender sent — an image, their own PDF — is pointed at directly. The bytes never
        pass through this page: the browser streams them, a large PDF does not sit in memory
        twice, and, more importantly, the response keeps the headers the server put on it. Those
        headers are the protection. Fetching the bytes and re-wrapping them in a blob: URL throws
        every one of them away, which is what used to happen here — the file said the PDF was
        sandboxed while the thing actually on screen was not.

        A converted document is fetched instead, because conversion is the step that can fail for
        an ordinary reason (a file nobody can open), and a person who is told why can press Save.
        An <iframe> pointed at a failure shows the failure, which helps nobody.

        Text is decoded here because only here is it known what to try: Turkish mail carries
        decades of Windows-1254, and a byte is not a letter until somebody decides which.
    */
    useEffect(() => {
        setUrl(null);
        setText(null);
        setError(null);
        if (!client || !emailId || !current?.partId || !kind) return;

        if (kind === 'image' || kind === 'pdf') {
            setUrl(client.attachmentUrl(emailId, current.partId, true));
            return;
        }

        let dropped = false;
        let made: string | null = null;

        void (async () => {
            try {
                if (kind === 'text') {
                    const blob = await client.downloadAttachment(emailId, current.partId);
                    const bytes = new Uint8Array(await blob.arrayBuffer());
                    if (!dropped) setText(decodeText(bytes));
                    return;
                }
                const res = await fetch(client.previewUrl(emailId, current.partId), {
                    credentials: 'same-origin',
                });
                if (!res.ok) throw new Error(await messageFrom(res));
                const blob = await res.blob();
                if (dropped) return;
                made = URL.createObjectURL(blob);
                setUrl(made);
            } catch (e: unknown) {
                if (!dropped) setError(e instanceof Error ? e.message : String(e));
            }
        })();

        return () => {
            dropped = true;
            if (made) URL.revokeObjectURL(made);
        };
    }, [client, emailId, current?.partId, current?.name, current?.type, kind]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
            else if (e.key === 'ArrowRight' && index < attachments.length - 1) onIndex(index + 1);
            else if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [index, attachments.length, onIndex, onClose]);

    if (!current) return null;

    /*
        Saving gives back what arrived, never what is on screen.

        A converted document is a copy made so it could be looked at; nobody wants a PDF of the
        spreadsheet they were sent. The link goes to the attachment itself, and the server's
        Content-Disposition names it — including the Turkish name, which is the whole reason that
        header is written the way it is.
    */
    const save = () => {
        if (!client || !emailId || !current?.partId) return;
        const link = document.createElement('a');
        link.href = client.attachmentUrl(emailId, current.partId);
        link.rel = 'noopener';
        link.click();
    };

    const nav: React.CSSProperties = {
        background: 'transparent',
        border: 'none',
        color: 'var(--text-dim)',
        fontSize: 'var(--fs-2xl)',
        padding: '0 10px',
        cursor: 'pointer',
    };

    return (
        <div
            data-testid="attachment-preview"
            data-print-hide
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 90,
                background: 'rgba(8, 7, 14, 0.72)',
                display: 'flex',
                flexDirection: 'column',
                padding: '28px 32px',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-panel)',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        borderBottom: '1px solid var(--border)',
                    }}
                >
                    <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {current.name ?? t('reader.unnamed')}
                    </span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--text-mute)' }}>
                        {fileSize(current.size, lang === 'tr' ? 'tr-TR' : 'en-GB')}
                    </span>
                    {attachments.length > 1 ? (
                        <>
                            <button style={nav} disabled={index === 0} onClick={() => onIndex(index - 1)} title={t('preview.previous')}>
                                ‹
                            </button>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--text-mute)' }}>
                                {index + 1}/{attachments.length}
                            </span>
                            <button
                                style={nav}
                                disabled={index === attachments.length - 1}
                                onClick={() => onIndex(index + 1)}
                                title={t('preview.next')}
                            >
                                ›
                            </button>
                        </>
                    ) : null}
                    {kind === 'document' ? (
                        // Said plainly, because it is not the file itself: an office suite's
                        // rendering is faithful, not identical, and somebody checking a figure
                        // against a printed copy should know which they are looking at.
                        <span className="kicker" title={t('preview.convertedWhy')}>
                            {t('preview.converted')}
                        </span>
                    ) : null}
                    <button className="btn" onClick={save}>
                        {t('preview.save')}
                    </button>
                    <button style={nav} onClick={onClose} title={t('app.close')}>
                        ×
                    </button>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface2)' }}>
                    {error ? (
                        <div style={{ color: 'var(--red)', fontSize: 'var(--fs-md)', padding: 20, textAlign: 'center' }}>
                            {error}
                        </div>
                    ) : kind === 'text' ? (
                        text === null ? (
                            <div className="kicker">// {t('app.loading')}</div>
                        ) : (
                            <pre
                                style={{
                                    margin: 0,
                                    padding: '16px 18px',
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'auto',
                                    fontFamily: 'var(--font-mono)',
                                    fontSize: 'var(--fs-sm)',
                                    lineHeight: 1.55,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: 'var(--text)',
                                }}
                            >
                                {text}
                            </pre>
                        )
                    ) : !url ? (
                        <div className="kicker">
                            // {kind === 'document' ? t('preview.converting') : t('app.loading')}
                        </div>
                    ) : kind === 'image' ? (
                        <img
                            src={url}
                            alt={current.name ?? ''}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <iframe
                            src={url}
                            title={current.name ?? 'PDF'}
                            style={{ width: '100%', height: '100%', border: 'none', background: '#ffffff' }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
